import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { Control, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Bank } from '@finley-technologies/finley-types';

import { addPricingOption, pricingOptionKeyByConvention } from './dataMappings';
import {
  BenchmarkPricingOptionModalForm,
  PricingOptionModalForm,
} from './types';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { SelectInput } from '~/core/routes/settings/implementations/common/Inputs';

const RATE_TYPE_TO_LABEL = {
  fixed: 'Fixed',
  benchmark: 'Benchmark',
};

export const useCanAddPricingOption = (
  control: Control<PricingOptionModalForm>,
): { canAdd: true } | { canAdd: false; reason: string | null } => {
  const formValues = useWatch({ control });
  const creditAgreement = useCreditAgreementStore(
    (store) => store.creditAgreement,
  );

  if (!formValues.rateType || !creditAgreement) {
    // Form is incomplete.
    return { canAdd: false, reason: null };
  }

  if (
    formValues.rateType === 'fixed' &&
    creditAgreement.loanMechanicsV1?.pricingOptions.some(
      (p) => p.type === 'fixed',
    )
  ) {
    return {
      canAdd: false,
      reason:
        'A fixed rate pricing option already exists. Edit the existing one.',
    };
  }

  if (formValues.rateType === 'benchmark') {
    if (!formValues.interestRateBenchmark) {
      // Form is incomplete.
      return { canAdd: false, reason: null };
    }
    if (
      creditAgreement.loanMechanicsV1?.pricingOptions.some(
        (p) =>
          p.type === 'benchmark' &&
          p.benchmark === formValues.interestRateBenchmark,
      )
    ) {
      return {
        canAdd: false,
        reason: `A ${formValues.interestRateBenchmark} benchmark pricing option already exists. Edit the existing one.`,
      };
    }
  }

  return { canAdd: true };
};

type AddPricingOptionModalProps = {
  disclosure: UseDisclosureReturn;
};

export const AddPricingOptionModal = ({
  disclosure,
}: AddPricingOptionModalProps) => {
  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );
  const form = useForm<PricingOptionModalForm>({
    mode: 'onTouched',
    shouldUnregister: true,
  });

  const rateType = useWatch({
    control: form.control,
    name: 'rateType',
  });
  const addResult = useCanAddPricingOption(form.control);

  const onAddPricingOption = useCallback(
    (pricingOption: PricingOptionModalForm) => {
      updateCreditAgreement((draft) => addPricingOption(pricingOption, draft));
    },
    [updateCreditAgreement],
  );

  const { isOpen, onClose } = disclosure;

  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) form.reset();
  }, [form, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent padding="20px">
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              onAddPricingOption(data);
              disclosure.onClose();
              navigate(`${pricingOptionKeyByConvention(data)}`);
            })}
          >
            <ModalHeader>Add Pricing Option</ModalHeader>
            <ModalCloseButton />
            <ModalBody display="flex" flexDirection="column" gap="1rem">
              <SelectInput<PricingOptionModalForm>
                label="Rate Type"
                name="rateType"
                placeholder="Select"
                options={Object.entries(RATE_TYPE_TO_LABEL).map(
                  ([rt, label]) => ({
                    label,
                    value: rt,
                  }),
                )}
                required
              />

              {rateType === 'benchmark' && (
                <SelectInput<BenchmarkPricingOptionModalForm>
                  label="Interest Rate Benchmark"
                  name="interestRateBenchmark"
                  placeholder="Select"
                  // todo: add correct FE type @markhaghani
                  options={Object.values(Bank.InterestRateBenchmark).map(
                    (benchmark) => ({
                      label: benchmark,
                      value: benchmark,
                    }),
                  )}
                  required
                />
              )}
              {!addResult.canAdd && addResult.reason ? (
                <Alert status="error">
                  <AlertIcon />
                  <AlertDescription>{addResult.reason}</AlertDescription>
                </Alert>
              ) : null}
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="v2-aurora"
                mr={3}
                onClick={onClose}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                colorScheme="v2-aurora"
                type="submit"
                isDisabled={!addResult.canAdd}
              >
                Add Pricing Option
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
